/**
 * @file        server-time.js  Find time offset between client & server for signed-post.
 *
 * @author      Ryan Saweczko, ryansaweczko@distributive.network
 * @date        Nov 2024
 */
'use strict';

const dcpEnv = require('dcp/common/dcp-env');

const { reallyJustFetch } = require('./just-fetch');
const { assert }          = require('dcp/common/dcp-assert');
const { requireNative }   = require('dcp/dcp-client/webpack-native-bridge');
const debugging           = require('dcp/debugging').scope('server-time');

exports.serverTimeAdjust = serverTimeAdjust;
exports.serverRelativeTime = serverRelativeTime;

/**
 * Wrapper class which marks time as being adjusted to the server's clock
 * @param {number|Date} localTime - the time according to the local machine
 * @param {number} offsetMs    - the difference between the local lock and server's clock
 * @param {URL} url - the URL the time is for; used only for display purposes
 */
function ServerTime(localTime, offsetMs, url)
{
  this.__localTimeT = Number(localTime);
  this.time = this.__localTimeT + offsetMs;
  this.server = url?.href ? url : new URL(url);

  assert(this.__localTimeT > 0, this.server.href);
  assert(typeof offsetMs === 'number');
}
ServerTime.prototype.valueOf = function ServerTime$$valueOf()
{
  return this.time;
}
ServerTime.prototype[require('util').inspect.custom] = function ServerTime$$inspect()
{
  return `[ServerTime ${this.time} ${this.url.origin}]`;
}
ServerTime.prototype.toLocalTime = function ServerTime$$toLocalTime()
{
  return this.__localTime;
}

const serverTimeCache = {}; /* time server href: { offsetMs, p$response, expiryTime } */

/**
 * Convert local time to server's time. On the first call, it will send a message to the server,
 * and use the `date` header to determine the client/server offset, for subsequent calls the offset is cached.
 *
 * The internal cache is keyed from the timeServiceLocation URL's href.  The cache expiry is checked on each
 * invocation, and refreshed if the invocation indicates stale cache data.
 *
 * @param {any}         localTime - time to convert to server's time. If this is passed as a number, it
 *                      is assumed to be the number of milliseconds since the epoch. If it is a Date
 *                      object, it is used directly. Any other argument (will be used as an argumen to
 *                      the Date constructor. (eg ISO date strings)
 *
 * @param {Object}      options - an object having the following properties:
 *                      {string|URL|DcpURL} timeServiceLocation: an endpoint on the server that responds
 *                                                               with the current time
 *
 * @returns {ServerTime}    an Object whose value is the number milliseconds since the epoch of the
 *                          localTime argument, with the difference between the local time and the
 *                          server time taken into account.
 */
async function serverTimeAdjust(localTime, options, debugLabel)
{
  if (localTime instanceof ServerTime) /* already adjusted */
    throw new Error(`time already adjusted for server ${localTime.url}`);
  if (typeof options === 'string')
    options = { timeServiceLocation: options };
  if (typeof options?.timeServiceLocation === 'object' && !(options.timeServiceLocation instanceof URL))
    options.timeServiceLocation = new URL(options.timeServiceLocation);
  if (!options?.timeServiceLocation)
  {
    if (dcp['dcp-build'].config.build === 'debug')
    {
      /* relax unknown clock offset severity for debug builds */
      console.warn('missing DCP time service location for', debugLabel || 'query');
      return new ServerTime(localTime, 0, new URL('http://localhost:123'));
    }
    throw new Error('missing DCP time service location for', debugLabel || 'query');
  }

  if (!Object.hasOwnProperty(options, 'ttl'))
    options = Object.assign({ ttl: 3600 }, options);
  if (!(localTime instanceof Date))
    localTime = new Date(localTime);
  const now = Date.now();
  const cache =
        serverTimeCache[options.timeServiceLocation.href] =
        (serverTimeCache[options.timeServiceLocation.href] || { lastUpdate: now });

  if (cache.lastUpdateNow + (options.ttl * 1e3) < now)
    return new ServerTime(localTime, cache.offsetMs, options.timeServiceLocation);

  if (cache.p$response)
  {
    await cache.p$response;
    assert(cache.offsetMs !== undefined);
    return new ServerTime(localTime, cache.offsetMs, options.timeServiceLocation);
  }

  let sendTime = Date.now();
  if (dcpEnv.platform === 'nodejs')
    requireNative('process').nextTick(() => sendTime = Date.now()); /* resample at bottom of event loop to reduce measurement of "ghost time" */

  cache.p$response = reallyJustFetch(options.timeServiceLocation, {
    method: 'GET',
    headers: {
      'cache-control': 'no-store, max-age=0',
    },
  });
  const response = await cache.p$response;
  const receiveTime = Date.now();

  // no response, assume no offset
  if (!response.responseText)
  {
    debugging() && console.debug(`Warning - no response from time service on ${options.timeServiceLocation}`);
    cache.offsetMs = 0;
  }
  else
  {
    const ro = JSON.parse(response.responseText);
    if (ro.success !== true)
      throw new Error(`invalid response to server-time request at ${options.timeServiceLocation}` + (ro.message ? ` (${ro.message})` : ''));

    // Offset = server time - client time + rtt/2 in seconds
    let halfRtt = (receiveTime - sendTime) / 2;
    if (halfRtt >= 0)
      cache.offsetMs = Math.floor((ro.now - receiveTime + halfRtt) / 1000);
    else
    {
      if (!Number.isFinite(halfRtt))
        throw new TypeError('error calculating RTT');
      /* local clock moved backwards during RTT calculation */
      await serverTimeAdjust(0, options.timeServiceLocation);
    }
  }

  return new ServerTime(localTime, cache.offsetMs, options.timeServiceLocation);
}

/**
 * @param {any}    now       instance of Date or argument to Date constructor
 * @param {object} options   see serverTimeAdjust
 * @param {number} internal  number of seconds from now, default=0
 *
 * @example
 * // calculate the time one hour from now on the client from the server's perspective
 * const keyExpireTime = serverRelativeTime(options, 3600);
 *
 * @returns ms since the epoch from now, plus interval, according to the server.
 */
function serverRelativeTime(options, interval, now)
{
  if (!(now instanceof Date))
    now = typeof now === 'undefined' ? new Date() : new Date(now);
  if (!interval)
    interval = 0;

  const localTime = Number(now) + (interval * 1e3);
  const cache = serverTimeCache[options.timeServiceLocation.href];
  if (!cache)
    throw new Error(`no server offset information available for ${options.timeServiceLocation.href}`);

  return localTime + cache.offsetMs;
}
